import { WabunkaTagListResponse } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_tag';

import { useAsyncFetchFromPublicWabunkaApi } from '@/composables/models/wabunka/useAsyncFetchFromPublicWabunkaApi';
import { useAsyncDataRequestStatus } from '@/composables/utils/useAsyncDataRequestStatus';
import { buildWabunkaTagListViewModel, type WabunkaTagDict, type WabunkaTagDictByGroupName, type WabunkaTagViewModel } from '@/models/wabunka/wabunkaTag';

type WabunkaTagsGroupType = 'theme' | 'destination' | 'preference';

export interface WabunkaTagsQuery {
  perPage: string;
}

/**
 * Wabunkaタグ一覧取得 Composable
 * Page でのみ使用可能
 */
export const useAsyncWabunkaTags = async (options?: { throwOnError?: boolean }) => {
  const path = computed(() => `/tags`);

  const queryRef = computed<WabunkaTagsQuery>(() => {
    const queryObj: WabunkaTagsQuery = {
      perPage: '0', // 0 で全件取得
    };
    return queryObj;
  });

  const { data, status, refresh } = await useAsyncFetchFromPublicWabunkaApi({
    path,
    query: queryRef,
    options: { server: true, immediate: true },
    converter: WabunkaTagListResponse.fromJSON,
    throwOnError: options?.throwOnError,
  });

  const tags = computed(() => buildWabunkaTagListViewModel(data.value?.items));

  const tagDictByWebPath = computed(() => {
    if (!tags.value) return {};
    return tags.value.reduce((acc: WabunkaTagDict, tag: WabunkaTagViewModel) => {
      acc[tag.webPath] = tag;
      return acc;
    }, {});
  });

  // タググループ名ごとにWabunkaTagListViewModelをグループ化
  const tagGroupsByType = (type: WabunkaTagsGroupType) =>
    computed((): WabunkaTagDictByGroupName => {
      if (!tags.value) return {};
      return tags.value
        .filter((tag) => tag.tagGroupType === type)
        .reduce((acc: WabunkaTagDictByGroupName, tag: WabunkaTagViewModel) => {
          const group = tag.tagGroupName;
          if (!acc[group]) {
            acc[group] = [];
          }
          acc[group].push(tag);
          return acc;
        }, {});
    });

  const groupTagsByTheme = (tagList: WabunkaTagViewModel[]): WabunkaTagDictByGroupName => {
    const result: WabunkaTagDictByGroupName = {};
    for (const tag of tagList) {
      if (tag.tagGroupType !== 'theme') continue;
      result[tag.tagGroupName] ??= [];
      result[tag.tagGroupName].push(tag);
    }
    return result;
  };

  const themeTagGroups = computed((): WabunkaTagDictByGroupName => {
    if (!tags.value) return {};
    return groupTagsByTheme(tags.value);
  });
  const destinationTagGroups = tagGroupsByType('destination');
  const preferenceTagGroups = tagGroupsByType('preference');

  return { themeTagGroups, destinationTagGroups, preferenceTagGroups, tags, tagDictByWebPath, refresh, ...useAsyncDataRequestStatus(status) };
};
